<script setup lang="ts">
    /**
     * ModalEP Component
     * This component is a modal dialog that can be used to display content in a popup window.
     * It supports various features such as custom titles, sizes, alignment, and event handling.
     * It is built using the Element Plus library.
     * @version 1.0.0
     * @author VIL
     * Modified by: VIL
     * @date 06/06/2025
     * @description 
     * Ajout de commentaires pour expliquer le fonctionnement du composant ModalEP.
     */

    import { computed, getCurrentInstance, onMounted, onUnmounted, ref } from 'vue';

    const instance = getCurrentInstance();

    const props = defineProps<{
        /**
         * If true, the modal will be visible.
         */
        visible: boolean;
        /**
         * The title of the modal.
         */
        title?: string;
        /**
         * The size of the modal.
         * Can be "small", "smallm", "medium", "large", "fullwidth", or "fullscreen".
         */
        size?: string;
        /**
         * If true, the modal will be aligned to the center.
         */
        alignCenter?: boolean;
        /**
         * If true, the modal will be appended to the body.
         * This is useful for modals that need to be displayed above other content.
         */
        appendToBody?: boolean;
        /**
         * If true, the modal will not close when pressing the Enter key.
         */
        disableEnterListen?: boolean;
        /**
         * If true, the modal will not close when pressing the Escape key.
         */
        disableEscapeListen?: boolean;
        /**
         * If true, the modal will close when clicking outside of it.
         * Defaults to true (Element Plus default behavior).
         */
        closeOnClickModal?: boolean;
        /**
         * A function that will be called before the modal is closed.
         * It can be used to perform cleanup or other actions before the modal is closed.
         */
        beforeClose?: (done:()=>void) => void;
    }>();

    const width = computed(() => {
        switch(props.size)
        {
            case "small":
                return "30%";
            case "smallm":
                return "40%";
            case "medium":
                return "50%";
            case "large":
                return "70%";
            case "fullwidth":
                return "90%";
            case "fullscreen":
                return "100%";
            default:
                return "50%";
        }
    })

    const isCloseOnClickModal = ref(props.closeOnClickModal ?? true);

    const hasEscapeListener = computed(() => {
        return !!instance?.vnode.props?.onEscape;
    });

    const handleEscape = () => {
        if (hasEscapeListener.value) {
            // Si un listener @escape est défini, on émet l'événement
            emit("escape");
        } else if (props.beforeClose) {
            // Si un beforeClose est défini, on l'appelle pour gérer la fermeture correctement
            props.beforeClose(() => {
                emit("update:visible", false);
            });
        } else {
            // Sinon, on ferme le dialog par défaut
            emit("update:visible", false);
        }
    };

    onMounted(() => {

        window.addEventListener("keyup", (e) => {

            if (e.key === "Escape" && props.visible && !props.disableEscapeListen) {
                handleEscape();
            }
            if ( e.key === "Enter" && props.visible && !props.disableEnterListen) {
                emit("enter");
            }
        });
    });

    onUnmounted(() => {
        window.removeEventListener("keyup", (e) => {
            if (e.key === "Escape" && props.visible ) {
                emit("escape");
            }
            if ( e.key === "Enter" && props.visible ) {
                emit("enter");
            }
        });

    });

    const emit = defineEmits<{
        /**
         * Emitted when the modal is closed.
         * This can be used to perform cleanup or other actions after the modal is closed.
         */
        (event: "close"): void;
        /**
         * Emitted when the visibility of the modal is updated.
         * This can be used to perform actions when the modal is shown or hidden.
         */
        (event: "before-close"): void;
        /**
         * Emitted when the visibility of the modal is updated.
         * This can be used to perform actions when the modal is shown or hidden.
         */
        (event: "update:visible", value: boolean): void;
        /**
         * Emitted when the Escape key is pressed while the modal is visible.
         * This can be used to perform actions when the Escape key is pressed.
         */
        (event: "escape"): void;
        /**
         * Emitted when the Enter key is pressed while the modal is visible.
         * This can be used to perform actions when the Enter key is pressed.
         */
        (event: "enter"): void;
    }>();

    const handleClose = () => {
        emit("close");
    };

    const handleVisible = (value: boolean) => {
        emit("update:visible", value);
    };

</script>

<template>
    <el-dialog destroy-on-close :close-on-press-escape="false" :close-on-click-modal="isCloseOnClickModal" :append-to-body="appendToBody" @close="handleClose" :align-center="alignCenter" :model-value="visible" @update:model-value="handleVisible" :before-close="beforeClose" :width="width">
        <template #header>
            <div class="modal-header">
                <div class="modal-title">
                    <slot name="title">
                        {{ title }}
                    </slot>
                </div>
                <div class="modal-header-btns">
                    <slot name="header-btns">
                        
                    </slot>
                </div>
            </div>
        </template>
        <slot name="body"></slot>
        <template #footer>
            <slot name="footer"></slot>
        </template>
        
    </el-dialog>
</template>

<style>

.modal-header {
    display: flex;
    justify-content: space-between; /* Aligne le titre à gauche et les boutons à droite */
    align-items: center; /* Centre verticalement le titre et les boutons */
}

.modal-title {
    font-size: 1.25rem; /* Taille du titre */
    font-weight: bold;
    margin: 0; /* Supprime les marges par défaut */
}

.modal-header-btns {
    display: flex;
    gap: 0.5rem; /* Ajoute un espacement entre les boutons */
    margin-right: 10px;
}

</style>